import React from 'react';
import clsx from 'clsx';
import { LinkIcon } from '@heroicons/react/20/solid';
import * as Headless from '@headlessui/react';
import type { Editor } from '@tiptap/react';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@oysterjs/ui/dialog';
import { Field, Label, Description, ErrorMessage } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { normalizeLinkUrl } from './normalize-link-url';

export const EditorLinkButton = ({
  editor,
  active,
  disabled
}: {
  editor: Editor;
  active: boolean;
  disabled: boolean;
}) => {
  const [selection, setSelection] = React.useState<{ from: number; to: number } | null>(null);
  const [url, setUrl] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const open = () => {
    const { from, to } = editor.state.selection;
    // The URL field takes focus, so keep the text range the sender chose.
    setSelection({ from, to });
    const href = editor.getAttributes('link').href as string | undefined;
    setUrl(href ?? '');
    setEditing(!!href);
    setError(null);
  };

  const apply = () => {
    if (!selection || editor.isDestroyed || !editor.isEditable) return;
    const value = url.trim();
    if (!value) {
      setError('Enter a URL for this link.');
      return;
    }
    const href = normalizeLinkUrl(value);
    const allowed = editor
      .can()
      .chain()
      .setTextSelection(selection)
      .extendMarkRange('link')
      .setLink({ href })
      .run();
    if (!allowed) {
      setError('Enter a valid link, such as https://example.com.');
      return;
    }
    const chain = editor.chain().focus().setTextSelection(selection).extendMarkRange('link');
    if (selection.from === selection.to && !editing) {
      chain
        .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
        .unsetMark('link')
        .run();
    } else {
      chain.setLink({ href }).run();
    }
    setSelection(null);
  };

  return (
    <>
      <Headless.Button
        type="button"
        onClick={open}
        disabled={disabled || !editor.isEditable}
        aria-label={active ? 'Edit link' : 'Insert link'}
        title={active ? 'Edit link' : 'Insert link'}
        className={clsx(
          'p-1 rounded-md',
          disabled
            ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            : 'hover:bg-neutral-200 dark:hover:bg-white/10',
          active
            ? 'text-primary-500 dark:text-primary-400'
            : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        <LinkIcon className="size-4" />
      </Headless.Button>
      <Dialog open={selection !== null} onClose={() => setSelection(null)} size="md">
        <DialogTitle>{editing ? 'Edit link' : 'Insert link'}</DialogTitle>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            apply();
          }}
        >
          <DialogBody>
            <Field>
              <Label>URL</Label>
              <Input
                data-autofocus
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setError(null);
                }}
                placeholder="https://example.com"
                inputMode="url"
                invalid={!!error}
              />
              <Description>
                {!editing && selection?.from === selection?.to
                  ? 'The URL will appear as the link text.'
                  : 'The selected text will link to this address.'}
              </Description>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Field>
          </DialogBody>
          <DialogActions>
            {editing && (
              <Button
                type="button"
                plain
                onClick={() => {
                  if (!selection || editor.isDestroyed || !editor.isEditable) return;
                  editor
                    .chain()
                    .focus()
                    .setTextSelection(selection)
                    .extendMarkRange('link')
                    .unsetLink()
                    .run();
                  setSelection(null);
                }}
              >
                Remove link
              </Button>
            )}
            <Button type="button" outline onClick={() => setSelection(null)}>
              Cancel
            </Button>
            <Button type="submit" color="sky">
              {editing ? 'Save link' : 'Insert link'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
